import React, { useState, useEffect, useCallback } from 'react';
import { Navbar, Nav, Container, NavDropdown, Button } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import UserSearch from './UserSearch';

export default function Navigation() {
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const { currentUser, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Base64 encoded default avatar
  const defaultAvatar = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiByeD0iMTYiIGZpbGw9IiM0ZjllZmYiLz4KPHN2ZyB4PSI4IiB5PSI2IiB3aWR0aD0iMTYiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0id2hpdGUiPgo8cGF0aCBkPSJNMTIgMTJjMy4zMSAwIDYtMi42OSA2LTZzLTIuNjktNi02LTYtNiAyLjY5IDYgNiAyLjY5IDYgNiA2em0wIDJjLTQuMDEgMC0xMiAyLjAxLTEyIDZ2MmgxNnYtMmMwLTMuOTktNy45OS02LTEyLTZ6Ii8+Cjwvc3ZnPgo8L3N2Zz4K";

  const loadUserProfile = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      setProfileLoading(true);
      const { user } = await api.getUserProfile(currentUser.username);
      setUserProfile(user);
    } catch (error) {
      console.error('Error loading user profile:', error);
      // If profile doesn't exist, that's ok
      if (error.message.includes('not found')) {
        setUserProfile(null);
      }
    } finally {
      setProfileLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  const handleLogout = async () => {
    try {
      setExpanded(false); // Close mobile menu
      await logout();
      navigate('/login');
    } catch {
      console.log('Failed to log out');
    }
  };

  const handleNavClick = (path) => {
    navigate(path);
    setExpanded(false); // Close mobile menu
  };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    if (path === '/profile') {
      return location.pathname === '/profile';
    }
    return false;
  };

  // Don't show navigation on login/signup pages
  if (location.pathname === '/login' || location.pathname === '/signup' || !currentUser) {
    return null;
  }

  return (
    <Navbar expand="lg" style={{ backgroundColor: '#1a1d29' }} variant="dark" fixed="top" expanded={expanded}>
      <Container>
        <Navbar.Brand 
          onClick={() => handleNavClick('/dashboard')} 
          style={{ color: '#4f9eff', fontWeight: 'bold', cursor: 'pointer' }}
        >
          Trade Central
        </Navbar.Brand>
        
        <Navbar.Toggle aria-controls="basic-navbar-nav" onClick={() => setExpanded(!expanded)} />
        
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="me-auto">
            <Nav.Link 
              onClick={() => handleNavClick('/dashboard')}
              style={{ color: isActive('/dashboard') ? '#4f9eff' : '#fff' }}
              className={isActive('/dashboard') ? 'fw-bold' : ''}
            >
              <i className="fas fa-home me-2"></i>
              <span className="d-lg-inline d-none">Dashboard</span>
              <span className="d-lg-none">Home</span>
            </Nav.Link>
            
            <Nav.Link 
              onClick={() => handleNavClick('/profile')}
              style={{ color: isActive('/profile') ? '#4f9eff' : '#fff' }}
              className={isActive('/profile') ? 'fw-bold' : ''}
            >
              <i className="fas fa-user me-2"></i>
              Profile
            </Nav.Link>
            
            <Nav.Link 
              onClick={() => handleNavClick('/scan')}
              style={{ color: location.pathname === '/scan' ? '#4f9eff' : '#fff' }}
              className={location.pathname === '/scan' ? 'fw-bold' : ''}
            >
              <i className="fas fa-camera me-2"></i>
              Scan
            </Nav.Link>
            
            <Nav.Link 
              onClick={() => handleNavClick('/profile/cards')}
              style={{ color: location.pathname === '/profile/cards' ? '#4f9eff' : '#fff' }}
              className={location.pathname === '/profile/cards' ? 'fw-bold' : ''}
            >
              <i className="fas fa-layer-group me-2"></i>
              My Cards
            </Nav.Link>
          </Nav>
          
          <Nav className="align-items-center">
            {/* User Search */}
            {currentUser && (
              <div className="me-3">
                <UserSearch />
              </div>
            )}
            
            {/* Theme Toggle Button */}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={toggleTheme}
              className="me-3 d-flex align-items-center"
              style={{ border: '1px solid #6c757d' }}
            >
              {theme === 'dark' ? (
                <>
                  <i className="bi bi-sun-fill me-2"></i>
                  Light
                </>
              ) : (
                <>
                  <i className="bi bi-moon-fill me-2"></i>
                  Dark
                </>
              )}
            </Button>
            
            {!profileLoading && userProfile ? (
              <NavDropdown 
                title={
                  <span className="d-flex align-items-center">
                    <img 
                      src={userProfile.profilePicture ? api.getImageUrl(userProfile.profilePicture) : defaultAvatar}
                      alt="Profile"
                      className="rounded-circle me-2"
                      width="32"
                      height="32"
                      style={{ objectFit: 'cover' }}
                    />
                    <span className="d-lg-inline d-none">@{userProfile.username}</span>
                  </span>
                }
                id="profile-nav-dropdown"
                align="end"
              >
                <NavDropdown.Header>
                  <strong>@{userProfile.username}</strong>
                  <br />
                  <small className="text-muted">{currentUser.email}</small>
                </NavDropdown.Header>
                
                <NavDropdown.Divider />
                
                <NavDropdown.Item onClick={() => handleNavClick('/profile')}>
                  <i className="fas fa-edit me-2"></i>
                  Edit Profile
                </NavDropdown.Item>
                
                <NavDropdown.Item onClick={() => handleNavClick(`/${userProfile.username}`)}>
                  <i className="fas fa-eye me-2"></i>
                  View Public Profile
                </NavDropdown.Item>
                
                <NavDropdown.Divider />
                
                <NavDropdown.Item onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt me-2"></i>
                  Logout
                </NavDropdown.Item>
              </NavDropdown>
            ) : (
              <Button variant="outline-light" size="sm" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt me-2"></i>
                Logout
              </Button>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
} 